import React, { SyntheticEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { TOptions } from 'i18next';
import { useMediaQuery } from 'react-responsive';

import ProductGalleryDesktop from './ProductGalleryDesktop';
import ProductGalleryMobile from './ProductGalleryMobile';
import ProductGalleryVideo from './ProductGalleryVideo';
import Image from 'components/Common/Medias/Image';
import useEnv from 'hooks/useEnv';
import useLargeSizeMode from 'hooks/ProductPageV2/useLargeSizeMode';
import { ENV_TWICPICS_URL_MEDIA } from 'settings/env';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from 'settings/imageSizes';
import { EXTRACT_UNIQ_MEDIA_CODE } from 'settings/regex';
import { Morphologies } from 'settings/productV2';
import {
  MorphologyType,
  ProductAttributesType,
  ProductVariantsAttributesType,
} from 'types/ProductPageV2/Products';
import {
  getCurvyImages,
  getCurvyVideo,
  getCurvyVideoVignette,
  getStandardImages,
  getStandardVideo,
  getStandardVideoVignette,
} from 'services/product';

import 'assets/styles/common/_productGallery.scss';
import styles from './ProductGallery.module.scss';

type ProductAttrType = ProductAttributesType & ProductVariantsAttributesType;

type Props = {
  /** List of product attributes */
  productAttributes: ProductAttrType;
  disableZoomMode?: boolean;
  classes?: { container?: string };
  onSlideAction?: (direction: 'prev' | 'next', e?: SyntheticEvent) => void;
  onSwitchMorphology?: (
    morphology: typeof Morphologies[keyof typeof Morphologies]
  ) => void;
};

/**
 * Display an image/video gallery with multiple images videos, zoom and carousel.
 */
function ProductGallery({
  productAttributes,
  classes,
  onSlideAction,
  onSwitchMorphology,
  disableZoomMode = false,
}: Props): JSX.Element | null {
  const { LSModeEnabled } = useLargeSizeMode();
  const [modeData, setModeData] = useState([]);
  const { urlWysiwygOctipas, urlTwicpicsVideo } = useEnv();
  const { t } = useTranslation();
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });

  const rendreGalleryItem = (
    type,
    src,
    thumb = '',
    alt = '',
    thumbAlt = '',
    morphology = ''
  ) => {
    const isVideo = type === 'video';
    const unavailableThumb = `${urlWysiwygOctipas}/visuel-indispo.jpg`;

    const srcSet = src || unavailableThumb;
    const thumbnail = thumb ? thumb : srcSet;
    const originalAlt = alt ? alt : srcSet;
    const thumbnailAlt = thumbAlt ? thumbAlt : srcSet;

    return {
      isVideo,
      srcSet,
      thumbnail,
      originalAlt,
      thumbnailAlt,
      renderItem: () => (
        <div>
          {isVideo ? (
            <ProductGalleryVideo
              srcPath={urlTwicpicsVideo + srcSet}
              title={originalAlt}
            />
          ) : (
            <Image
              alt={originalAlt}
              className={classNames(
                'image-gallery-image',
                disableZoomMode && 'disable-zoom'
              )}
              src={`${ENV_TWICPICS_URL_MEDIA}${srcSet}`}
            />
          )}
        </div>
      ),
      renderThumbInner: () => (
        <span className={`image-gallery-thumbnail-inner`}>
          <Image
            alt={thumbnailAlt}
            className="image-gallery-thumbnail-image"
            ratio={`${THUMBNAIL_WIDTH.toString()}/${THUMBNAIL_HEIGHT.toString()}`}
            src={`${ENV_TWICPICS_URL_MEDIA}${thumbnail}`}
          />
        </span>
      ),
      morphology: morphology,
    };
  };

  let incrementNoCodeMedia = 0;

  /**
   * Prepare the media list to get src and alt
   *
   * @param mediaPaths
   * @param attributes
   * @param isCurvy
   */
  const prepareMediasData = (
    mediaPaths: string[],
    attributes: Record<string, string>,
    isCurvy: boolean
  ): { src: string; alt: string }[] => {
    return mediaPaths.map(path => prepareMediaData(path, attributes, isCurvy));
  };

  /**
   * Prepare the media to get src and alt
   *
   * @param path
   * @param attributes
   * @param isCurvy
   */
  const prepareMediaData = (
    path: string,
    attributes: Record<string, string>,
    isCurvy: boolean
  ): { src: string; alt: string } => {
    const hasGender = !!attributes?.gender;

    let i18nKey = 'ALT_MEDIA_FORMAT'; // Select a default value (hasGender and is not curvy)
    if (isCurvy) {
      i18nKey = hasGender
        ? 'ALT_MEDIA_FORMAT_CURVY'
        : 'ALT_MEDIA_FORMAT_CURVY_WITHOUT_GENDER';
    } else if (!hasGender) {
      i18nKey = 'ALT_MEDIA_FORMAT_WITHOUT_GENDER';
    }

    const regexExec = EXTRACT_UNIQ_MEDIA_CODE.exec(path);
    const increment = regexExec?.groups?.codeMedia || ++incrementNoCodeMedia;

    return {
      src: path,
      alt: t(i18nKey, { ...attributes, increment: increment } as TOptions<
        Record<string, string>
      >),
    };
  };

  const standardVideoUrl = getStandardVideo(productAttributes);
  const standardVideoImage = getStandardVideoVignette(productAttributes);
  const standardOtherImagesList = getStandardImages(productAttributes);

  const curvyVideoUrl = getCurvyVideo(productAttributes);
  const curvyVideoImage = getCurvyVideoVignette(productAttributes);
  const curvyOtherImagesList = getCurvyImages(productAttributes);

  const prepareMediasDataAttr = {
    subtitle: productAttributes.libelle.Value,
    gender: productAttributes.gender.Value,
    baseColour: !!productAttributes.couleurStandard.Value
      ? productAttributes.couleurStandard.Value
      : productAttributes.couleur.Value,
    modelName: productAttributes.nomModele.Value,
  };

  const standardGalleryData = prepareMediasData(
    standardOtherImagesList,
    prepareMediasDataAttr,
    false
  ).map(({ src, alt }) =>
    rendreGalleryItem('image', src, '', alt, alt, Morphologies.STANDARD)
  );

  const curvyGalleryData = prepareMediasData(
    curvyOtherImagesList,
    prepareMediasDataAttr,
    true
  ).map(({ src, alt }) =>
    rendreGalleryItem('image', src, '', alt, alt, Morphologies.CURVY)
  );

  let standardVideoData = null;
  if (standardVideoUrl) {
    const { alt, src } = prepareMediaData(
      standardVideoUrl,
      prepareMediasDataAttr,
      false
    );

    standardVideoData = rendreGalleryItem(
      'video',
      src,
      standardVideoImage || undefined,
      alt,
      alt,
      Morphologies.STANDARD
    );
  }

  let curvyVideoData = undefined;
  if (curvyVideoUrl) {
    const { alt, src } = prepareMediaData(
      curvyVideoUrl,
      prepareMediasDataAttr,
      true
    );

    curvyVideoData = rendreGalleryItem(
      'video',
      src,
      curvyVideoImage || undefined,
      alt,
      alt,
      Morphologies.CURVY
    );
  }

  standardVideoData && standardGalleryData.splice(1, 0, standardVideoData);
  curvyVideoData && curvyGalleryData.splice(1, 0, curvyVideoData);

  const hasStandard = standardGalleryData.length > 0;
  const hasCurvy = curvyGalleryData.length > 0;
  const morphoPriority = productAttributes.MORPHOPRIORITY?.Value;
  const [
    selectedMorphologyType,
    setSelectedMorphologyType,
  ] = useState<MorphologyType>(
    hasCurvy ? Morphologies.CURVY : Morphologies.STANDARD
  );

  useEffect(() => {
    if (LSModeEnabled && hasCurvy) {
      setModeData(curvyGalleryData);
      setSelectedMorphologyType(
        (curvyGalleryData[0]?.morphology as MorphologyType) ||
          Morphologies.CURVY
      );
    } else if (
      (morphoPriority === Morphologies.CURVY && hasCurvy) ||
      (!hasStandard && !hasCurvy)
    ) {
      const list = [...curvyGalleryData, ...standardGalleryData];
      setModeData(list);
      setSelectedMorphologyType(
        (list[0]?.morphology as MorphologyType) || Morphologies.CURVY
      );
    } else {
      const list = [...standardGalleryData, ...curvyGalleryData];
      setModeData(list);
      setSelectedMorphologyType(
        (list[0]?.morphology as MorphologyType) || Morphologies.STANDARD
      );
    }
  }, [productAttributes]);

  const buttonSwitchHandler = () => {
    if (selectedMorphologyType == Morphologies.STANDARD) {
      setModeData([...curvyGalleryData, ...standardGalleryData]);
      setSelectedMorphologyType(Morphologies.CURVY);
    } else {
      setModeData([...standardGalleryData, ...curvyGalleryData]);
      setSelectedMorphologyType(Morphologies.STANDARD);
    }

    onSwitchMorphology?.(
      morphoPriority === Morphologies.CURVY
        ? Morphologies.STANDARD
        : Morphologies.CURVY
    );
  };

  if (!modeData.length) return null;

  return (
    <div
      className={classNames(styles.productGalleryContainer, classes?.container)}
    >
      {isMobileAndPortraitTablet && (
        <ProductGalleryMobile
          data={modeData}
          selectedMorphologyType={setSelectedMorphologyType}
          disableZoomMode={disableZoomMode}
          onSlideAction={onSlideAction}
        />
      )}
      {!isMobileAndPortraitTablet && (
        <ProductGalleryDesktop
          data={modeData}
          selectedMorphologyType={setSelectedMorphologyType}
          onSlideAction={onSlideAction}
          disableZoomMode={disableZoomMode}
        />
      )}
      {hasCurvy && hasStandard && !LSModeEnabled && (
        <div className={styles.modelInfoContainer}>
          <button onClick={buttonSwitchHandler}>
            {t('SEE_ON_ANOTHER_MODEL')}
          </button>
        </div>
      )}
    </div>
  );
}

export default ProductGallery;
